import { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import {
  formatTypedDate,
  parseCroatianDate,
  parseCroatianDateTime,
  toCroatianDateInput
} from '../lib/croatian-date';

interface CroatianDateInputProps {
  value: string;
  onValueChange: (value: string) => void;
  withTime?: boolean;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
}

export function CroatianDateInput({
  value,
  onValueChange,
  withTime = false,
  required,
  disabled,
  min,
  max,
  className,
  ariaLabel,
  autoFocus
}: CroatianDateInputProps) {
  const [text, setText] = useState(() => toCroatianDateInput(value, withTime));
  const [invalid, setInvalid] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(toCroatianDateInput(value, withTime));
    setInvalid(false);
  }, [value, withTime]);

  function change(raw: string) {
    const nextText = formatTypedDate(raw, withTime);
    setText(nextText);
    setInvalid(false);
    if (!nextText) {
      onValueChange('');
      return;
    }
    const parsed = withTime
      ? parseCroatianDateTime(nextText)
      : parseCroatianDate(nextText);
    if (parsed) onValueChange(parsed);
  }

  function validate() {
    if (!text) {
      setInvalid(Boolean(required));
      return;
    }
    setInvalid(!(withTime ? parseCroatianDateTime(text) : parseCroatianDate(text)));
  }

  function showPicker() {
    const input = picker.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
      input.click();
    }
  }

  return (
    <span className={`croatian-date-input ${invalid ? 'is-invalid' : ''} ${className ?? ''}`}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        value={text}
        onChange={(event) => change(event.target.value)}
        onBlur={validate}
        placeholder={withTime ? 'dd.mm.gggg. hh:mm' : 'dd.mm.gggg.'}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        pattern={withTime ? '\\d{2}\\.\\d{2}\\.\\d{4}\\. \\d{2}:\\d{2}' : '\\d{2}\\.\\d{2}\\.\\d{4}\\.'}
        title={withTime ? 'Upišite datum kao dd.mm.gggg. hh:mm' : 'Upišite datum kao dd.mm.gggg.'}
      />
      <button type="button" onClick={showPicker} disabled={disabled} aria-label="Otvori kalendar">
        <CalendarDays />
      </button>
      <input
        ref={picker}
        className="croatian-date-input__picker"
        tabIndex={-1}
        type={withTime ? 'datetime-local' : 'date'}
        value={value}
        min={min}
        max={max}
        onChange={(event) => onValueChange(event.target.value)}
        aria-hidden="true"
      />
    </span>
  );
}
